import { createFileRoute } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Badge,
  Button,
  Card,
  CardContent,
  ConfirmModal,
  Input,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Textarea,
} from '@ouropix/ui';
import {
  Users,
  Search,
  ShieldOff,
  ShieldCheck,
  ChevronLeft,
  ChevronRight,
  MousePointerClick,
  UserPlus,
  Wallet,
} from 'lucide-react';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { toast } from 'sonner';
import { api } from '@/lib/api';

export const Route = createFileRoute('/_admin/afiliados')({
  component: AfiliadosPage,
});

interface AffiliateRow {
  id: string;
  code: string;
  name: string;
  description: string;
  active: boolean;
  conversions: number;
  clicks: number;
  total_commission: string; // cents (BigInt-safe)
  created_at: string;
  updated_at: string;
  created_by: string;
}

interface AffiliatesResponse {
  rows: AffiliateRow[];
  pagination: {
    total: number;
    page: number;
    limit: number;
    total_pages: number;
  };
}

const PAGE_SIZE = 25;

function centsToBRL(reais: string | undefined): string {
  // Nome historico — backend ja retorna em reais (Decimal). Sem fator 100.
  if (!reais) return 'R$ 0,00';
  const n = Number(reais);
  if (!Number.isFinite(n)) return 'R$ 0,00';
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  }).format(n);
}

function formatDate(value: string | null | undefined): string {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  return format(d, 'dd/MM/yyyy', { locale: ptBR });
}

function AfiliadosPage() {
  const queryClient = useQueryClient();
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [page, setPage] = useState(1);
  const [pendingBan, setPendingBan] = useState<AffiliateRow | null>(null);
  const [pendingUnban, setPendingUnban] = useState<AffiliateRow | null>(null);
  const [banReason, setBanReason] = useState('');

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(searchTerm.trim());
      setPage(1);
    }, 300);
    return () => clearTimeout(handler);
  }, [searchTerm]);

  const { data, isLoading, error } = useQuery<AffiliatesResponse>({
    queryKey: ['admin-affiliates', { search: debouncedSearch, page }],
    queryFn: async () => {
      const { data } = await api.get('/admin/affiliates', {
        params: {
          search: debouncedSearch || undefined,
          page,
          limit: PAGE_SIZE,
        },
      });
      return data;
    },
    staleTime: 30_000,
  });

  const banMutation = useMutation({
    mutationFn: async ({ id, reason }: { id: string; reason: string }) => {
      await api.post(`/admin/affiliates/${id}/ban`, { reason });
    },
    onSuccess: () => {
      toast.success('BTag banida');
      queryClient.invalidateQueries({ queryKey: ['admin-affiliates'] });
      queryClient.invalidateQueries({ queryKey: ['btags'] });
      setPendingBan(null);
      setBanReason('');
    },
    onError: (err: any) => {
      toast.error(
        err?.response?.data?.message ?? 'Erro ao banir BTag',
      );
    },
  });

  const unbanMutation = useMutation({
    mutationFn: async ({ id }: { id: string }) => {
      await api.post(`/admin/affiliates/${id}/unban`);
    },
    onSuccess: () => {
      toast.success('BTag desbanida');
      queryClient.invalidateQueries({ queryKey: ['admin-affiliates'] });
      queryClient.invalidateQueries({ queryKey: ['btags'] });
      setPendingUnban(null);
    },
    onError: (err: any) => {
      toast.error(
        err?.response?.data?.message ?? 'Erro ao desbanir BTag',
      );
    },
  });

  const rows = data?.rows ?? [];
  const totalPages = data?.pagination?.total_pages ?? 0;

  // Aggregates pra cards de KPI (page atual)
  const totalConversions = rows.reduce((acc, r) => acc + r.conversions, 0);
  const totalClicks = rows.reduce((acc, r) => acc + r.clicks, 0);
  const totalCommissionCents = rows.reduce((acc, r) => {
    const n = Number(r.total_commission);
    return acc + (Number.isFinite(n) ? n : 0);
  }, 0);

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-3">
          <Users className="h-5 w-5 text-primary" />
          <div>
            <h1 className="text-2xl font-bold">Afiliados</h1>
            <p className="text-muted-foreground text-sm">
              Gestão de BTags com estatísticas agregadas e ban/unban
            </p>
          </div>
        </div>
      </div>

      {/* KPIs (pagina atual) */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-blue-500/10">
                <MousePointerClick className="h-4 w-4 text-blue-500" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Cliques (página)</p>
                <p className="text-xl font-bold">{totalClicks}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-green-500/10">
                <UserPlus className="h-4 w-4 text-green-500" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Conversões (página)</p>
                <p className="text-xl font-bold">{totalConversions}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-amber-500/10">
                <Wallet className="h-4 w-4 text-amber-500" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Comissão total (página)</p>
                <p className="text-xl font-bold">
                  {centsToBRL(String(totalCommissionCents))}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="relative max-w-sm">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          placeholder="Buscar por nome ou descrição..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="pl-9"
        />
      </div>

      {error && (
        <Card>
          <CardContent className="p-8 text-center space-y-2">
            <p role="alert" className="text-destructive font-medium">
              Erro ao carregar afiliados
            </p>
            <p className="text-sm text-muted-foreground">
              {(error as Error).message}
            </p>
          </CardContent>
        </Card>
      )}

      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Código</TableHead>
              <TableHead>Campanha</TableHead>
              <TableHead className="text-right">Cliques</TableHead>
              <TableHead className="text-right">Conversões</TableHead>
              <TableHead className="text-right">Comissão</TableHead>
              <TableHead className="hidden md:table-cell">Criado em</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-right">Ações</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={8} className="text-center py-12 text-muted-foreground">
                  Carregando...
                </TableCell>
              </TableRow>
            ) : rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} className="text-center py-12 text-muted-foreground">
                  {debouncedSearch
                    ? 'Nenhuma campanha encontrada para esta busca.'
                    : 'Nenhuma campanha cadastrada.'}
                </TableCell>
              </TableRow>
            ) : (
              rows.map((row) => (
                <TableRow key={row.id}>
                  <TableCell>
                    <Badge variant="outline" className="font-mono">
                      {row.code}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <div>
                      <p className="font-medium">{row.name}</p>
                      {row.description && (
                        <p className="text-xs text-muted-foreground truncate max-w-[280px]">
                          {row.description}
                        </p>
                      )}
                    </div>
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    {row.clicks}
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    {row.conversions}
                  </TableCell>
                  <TableCell className="text-right tabular-nums font-medium">
                    {centsToBRL(row.total_commission)}
                  </TableCell>
                  <TableCell className="hidden md:table-cell text-sm text-muted-foreground">
                    {formatDate(row.created_at)}
                  </TableCell>
                  <TableCell>
                    {row.active ? (
                      <Badge className="bg-green-500/10 text-green-500 border-green-500/30">
                        Ativa
                      </Badge>
                    ) : (
                      <Badge className="bg-red-500/10 text-red-500 border-red-500/30">
                        Banida
                      </Badge>
                    )}
                  </TableCell>
                  <TableCell className="text-right">
                    {row.active ? (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setPendingBan(row)}
                        className="text-destructive hover:text-destructive"
                      >
                        <ShieldOff className="h-4 w-4 mr-1" />
                        Banir
                      </Button>
                    ) : (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setPendingUnban(row)}
                      >
                        <ShieldCheck className="h-4 w-4 mr-1" />
                        Desbanir
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-muted-foreground">
            Página {page} de {totalPages} — {data?.pagination?.total ?? 0} no total
          </p>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page <= 1}
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page >= totalPages}
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}

      {/* Ban modal */}
      <ConfirmModal
        isOpen={pendingBan !== null}
        onClose={() => {
          setPendingBan(null);
          setBanReason('');
        }}
        onConfirm={() => {
          if (pendingBan) {
            banMutation.mutate({ id: pendingBan.id, reason: banReason });
          }
        }}
        title={`Banir BTag ${pendingBan?.code ?? ''}?`}
        description={
          <div className="space-y-3">
            <p>
              Esta ação desativa a BTag <span className="font-medium">{pendingBan?.name}</span>.
              Cadastros futuros via essa campanha não serão atribuídos. Você pode desbanir depois.
            </p>
            <div className="space-y-1.5 text-left">
              <label htmlFor="ban-reason" className="text-xs font-medium text-foreground">
                Motivo (opcional, gravado em audit log)
              </label>
              <Textarea
                id="ban-reason"
                value={banReason}
                onChange={(e) => setBanReason(e.target.value)}
                placeholder="ex: spam de cadastros, fraude detectada"
                rows={3}
                disabled={banMutation.isPending}
              />
            </div>
          </div>
        }
        confirmText="Banir BTag"
        cancelText="Cancelar"
        variant="destructive"
        isLoading={banMutation.isPending}
      />

      {/* Unban modal */}
      <ConfirmModal
        isOpen={pendingUnban !== null}
        onClose={() => setPendingUnban(null)}
        onConfirm={() => {
          if (pendingUnban) {
            unbanMutation.mutate({ id: pendingUnban.id });
          }
        }}
        title={`Desbanir BTag ${pendingUnban?.code ?? ''}?`}
        description={
          <p>
            A BTag <span className="font-medium">{pendingUnban?.name}</span> voltará a aceitar cadastros.
          </p>
        }
        confirmText="Desbanir"
        cancelText="Cancelar"
        isLoading={unbanMutation.isPending}
      />
    </div>
  );
}
